import { API } from "@/api/apiEndPoints";

export const MAX_INSPECTION_STEPS = 10;

/**
 * Full POST URL for IQ /suggest-inspection-protocol/.
 * @param {string} iqBaseUrl
 * @returns {string}
 */
export function buildIqSuggestInspectionProtocolEndpoint(iqBaseUrl) {
  return `${String(iqBaseUrl || "")
    .trim()
    .replace(/\/$/, "")}/${API.SUGGEST_INSPECTION_PROTOCOL}`;
}

/**
 * Build POST body for /suggest-inspection-protocol/.
 * @param {Object} params
 * @param {string} params.checkpointCriteria
 * @param {string} params.microlearningContent
 * @param {string[]} [params.existingSteps]
 * @returns {Object}
 */
export function buildSuggestInspectionProtocolPayload({
  checkpointCriteria,
  microlearningContent,
  existingSteps = [],
}) {
  return {
    checkpoint_criteria: String(checkpointCriteria || "").trim(),
    microlearning_content: String(microlearningContent || "").trim(),
    existing_steps: Array.isArray(existingSteps)
      ? existingSteps.map((step) => String(step || "").trim()).filter(Boolean)
      : [],
  };
}

/**
 * Extract inspector-facing step descriptions for duplicate avoidance.
 * @param {Array<Object>} steps
 * @returns {string[]}
 */
export function extractExistingInspectionStepDescriptions(steps) {
  if (!Array.isArray(steps)) return [];
  return steps
    .map((step) => {
      const lookFor = step?.look_for ?? step?.what_to_look_for ?? "";
      const action = step?.inspector_action ?? "";
      return (lookFor || action).trim();
    })
    .filter(Boolean);
}

function normalizeCompetenceCategory(stepType) {
  const normalized = String(stepType || "ABILITY").toLowerCase();
  if (normalized === "behavior") return "behaviour";
  if (normalized === "knowledge" || normalized === "ability" || normalized === "behaviour") {
    return normalized;
  }
  return "ability";
}

/**
 * Map IQ /suggest-inspection-protocol/ steps to InspectionEditor row shape.
 * @param {Array<Object>} steps
 * @returns {Array<Object>}
 */
export function mapSuggestedStepsToEditorRows(steps) {
  if (!Array.isArray(steps)) return [];

  return steps
    .map((step) => {
      const lookFor = String(step?.what_to_look_for ?? "").trim();
      if (!lookFor) return null;

      return {
        competence_category: normalizeCompetenceCategory(step?.step_type),
        inspector_action: lookFor,
        look_for: lookFor,
        pass_criteria: String(step?.pass_criteria ?? "").trim(),
        fail_criteria: String(step?.fail_criteria ?? "").trim(),
        timestamp_ref: "",
        video_jump_url: "",
      };
    })
    .filter(Boolean);
}

/**
 * Drop suggested rows whose look_for duplicates an existing step (case-insensitive).
 * @param {Array<Object>} suggestedRows
 * @param {Array<Object>} existingSteps
 * @returns {Array<Object>}
 */
export function filterDuplicateSuggestedInspectionSteps(suggestedRows, existingSteps = []) {
  const existingLookFors = new Set(
    (Array.isArray(existingSteps) ? existingSteps : [])
      .map((step) =>
        String(step?.look_for ?? step?.what_to_look_for ?? "")
          .trim()
          .toLowerCase()
      )
      .filter(Boolean)
  );

  const seenSuggested = new Set();
  return (Array.isArray(suggestedRows) ? suggestedRows : []).filter((row) => {
    const lookFor = String(row?.look_for ?? "")
      .trim()
      .toLowerCase();
    if (!lookFor || existingLookFors.has(lookFor) || seenSuggested.has(lookFor)) {
      return false;
    }
    seenSuggested.add(lookFor);
    return true;
  });
}
